import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader, Panel, Button, Field, FormRow, Banner, useToast } from '../../ds/index'
import { useApi } from '../../lib/useApi'
import { post, patch, del } from '../../lib/api'
import { money, dateShort } from '../../lib/format'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'
import { pct, type Summary } from './ClientOverview'
import { TierBadge } from './ClientLeads'
import { ProfileTabs } from './ClientProfile'
import { MIN_PASSWORD } from '../../lib/password'

type Account = { id: string; name: string; email: string; phone: string | null; role: string; created_at: string; sessions: number; password_changed_at: string | null }

/** Dollars typed by a person into whole cents, or null for blank. NaN means it is not a number. */
const toCents = (s: string) => {
  const t = s.replace(/[$,\s]/g, '')
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : NaN
}

export default function ClientSettings() {
  return (
    <>
      <PageHeader title="Your profile" subtitle="Which leads you take, what happens when your credit runs out, and your own login." />
      <ProfileTabs />
      <ServiceZone />
      <div style={{ height: 'var(--s-6)' }} />
      <Buying />
      <div style={{ height: 'var(--s-7)' }} />
      <YourAccount />
    </>
  )
}

function Buying() {
  const { data, error, reload } = useApi<Summary>('/api/client/summary')
  const toast = useToast()
  const [tiers, setTiers] = useState<string[]>([])
  const [weekly, setWeekly] = useState('')
  const [autoBuy, setAutoBuy] = useState(false)
  const [cap, setCap] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!data) return
    setTiers(data.client.tiers_wanted ?? data.tiers.map((t) => t.key))
    setWeekly(data.client.weekly_lead_limit != null ? String(data.client.weekly_lead_limit) : '')
    setAutoBuy(data.wallet.auto_buy)
    setCap(data.wallet.auto_buy_cap_cents != null ? String(data.wallet.auto_buy_cap_cents / 100) : '')
  }, [data])

  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />

  const toggle = (key: string, on: boolean) => setTiers((t) => (on ? [...t, key] : t.filter((k) => k !== key)))

  const save = async () => {
    setErr(null)
    if (tiers.length === 0) { setErr('Pick at least one tier, or you will not get any leads.'); return }
    const limit = weekly.trim() ? Number(weekly) : null
    if (limit !== null && (!Number.isInteger(limit) || limit < 1)) { setErr('The weekly limit has to be a whole number of leads, or left blank.'); return }
    const capCents = toCents(cap)
    if (Number.isNaN(capCents)) { setErr('The monthly limit has to be a dollar amount.'); return }
    const all = data.tiers.every((t) => tiers.includes(t.key))
    setBusy(true)
    try {
      await patch('/api/client/buying', {
        tiers_wanted: all ? null : data.tiers.filter((t) => tiers.includes(t.key)).map((t) => t.key),
        weekly_lead_limit: limit,
        auto_buy_cap_cents: autoBuy ? capCents : undefined,
      })
      toast('Saved', 'pos')
      reload()
    } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); save() }} className="stack">
      <Panel>
        <div className="panel-header"><h2 className="panel-title">Leads you take</h2><span className="muted">tap a tier to turn it on or off</span></div>
        <div className="tier-picks" role="group" aria-label="Tiers you take">
          {data.tiers.map((t) => {
            const on = tiers.includes(t.key)
            return (
              <button key={t.key} type="button" className="tier-pick" aria-pressed={on} onClick={() => toggle(t.key, !on)}>
                <TierBadge name={t.name} tierKey={t.key} large />
                <span className="stack-tight" style={{ gap: 2 }}>
                  <span className="tier-pick-price">{money(t.price_cents)}</span>
                  <span className="muted" style={{ fontSize: 12 }}>{on ? 'You take these' : 'Not sent to you'}</span>
                </span>
                {t.description && <span className="tier-pick-desc">{t.description}</span>}
              </button>
            )
          })}
        </div>
        <div style={{ marginTop: 'var(--s-6)', maxWidth: 360 }}>
          <FormRow label="Most leads in a week" htmlFor="weekly-limit" help="Leave blank for no limit.">
            <input id="weekly-limit" className="field" inputMode="numeric" value={weekly} onChange={(e) => setWeekly(e.target.value)} placeholder="No limit" />
          </FormRow>
        </div>
      </Panel>

      <Panel>
        <div className="panel-header"><h2 className="panel-title">When your credit runs out</h2>{autoBuy ? <span className="pill is-good">Leads keep coming</span> : <span className="pill is-warn">Leads pause</span>}</div>
        {autoBuy ? (
          <p style={{ marginTop: 0 }}>
            Leads keep coming. Each one uses your credit first, at {pct(data.wallet.discount_bps)} off. With no credit left it comes anyway, at full price, on the bill on the 1st.
            To keep the discount, add money or turn on <Link to="/portal/billing">auto-refill</Link>.
          </p>
        ) : (
          <p style={{ marginTop: 0 }}>Leads pause when your credit runs out. {data.org.name} set this for your account; ask them to change it.</p>
        )}
        {autoBuy && (
          <div style={{ maxWidth: 360 }}>
            <FormRow label="Most on the bill in a month, dollars (optional)" htmlFor="auto-cap" help="Leave blank for no limit. Past it, leads pause until next month or until you add credit.">
              <input id="auto-cap" className="field" inputMode="decimal" value={cap} onChange={(e) => setCap(e.target.value)} placeholder="No limit" />
            </FormRow>
          </div>
        )}
      </Panel>

      {err && <Banner tone="neg">{err}</Banner>}
      <div><Button type="submit" disabled={busy}>{busy ? 'Saving' : 'Save lead settings'}</Button></div>
    </form>
  )
}

function YourAccount() {
  const { data, error, reload } = useApi<{ account: Account; viewing_as: unknown }>('/api/account')
  const toast = useToast()
  const [profile, setProfile] = useState({ name: '', phone: '' })
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' })
  const [pwErr, setPwErr] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => { if (data) setProfile({ name: data.account.name, phone: data.account.phone || '' }) }, [data])
  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  const a = data.account
  const locked = Boolean(data.viewing_as)

  const saveProfile = async () => {
    setBusy('profile')
    try { await patch('/api/account', { name: profile.name.trim(), phone: profile.phone.trim() }); toast('Saved', 'pos'); reload() }
    catch (e) { toast((e as Error).message, 'neg') }
    finally { setBusy(null) }
  }
  const changePassword = async () => {
    setPwErr(null)
    if (pw.next !== pw.confirm) { setPwErr('The two new passwords do not match'); return }
    setBusy('pw')
    try {
      await post('/api/account/password', { current_password: pw.current, new_password: pw.next })
      setPw({ current: '', next: '', confirm: '' })
      toast('Password changed', 'pos')
      reload()
    } catch (e) { setPwErr((e as Error).message) } finally { setBusy(null) }
  }

  return (
    <>
      {locked && <div style={{ marginBottom: 'var(--s-6)' }}><Banner tone="warn" title={`This is ${a.name}'s login`}>You are viewing as them, so changes here are off.</Banner></div>}
      <div className="two-col">
        <Panel>
          <div className="panel-header"><h2 className="panel-title">Your account</h2></div>
          <div className="stack-tight">
            <Field label="Name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} disabled={locked} />
            <Field label="Phone" type="tel" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} disabled={locked} />
            <div className="readonly-row"><span className="s-label muted">Sign-in email</span><span>{a.email}</span></div>
            <div><Button onClick={saveProfile} disabled={locked || busy !== null || !profile.name.trim()}>{busy === 'profile' ? 'Saving' : 'Save'}</Button></div>
          </div>
        </Panel>

        <Panel>
          <div className="panel-header"><h2 className="panel-title">Password</h2><span className="muted">{a.password_changed_at ? `changed ${dateShort(a.password_changed_at)}` : 'never changed'}</span></div>
          <form className="stack-tight" onSubmit={(e) => { e.preventDefault(); changePassword() }}>
            <input type="text" autoComplete="username" value={a.email} readOnly hidden />
            <Field label="Current password" type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} disabled={locked} />
            <Field label="New password" type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} disabled={locked} />
            <Field label="New password again" type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} disabled={locked} />
            <p className="form-help" style={{ margin: 0 }}>At least {MIN_PASSWORD} characters.</p>
            {pwErr && <p className="form-error" role="alert">{pwErr}</p>}
            <div><Button type="submit" disabled={locked || busy !== null || !pw.current || pw.next.length < MIN_PASSWORD || !pw.confirm}>{busy === 'pw' ? 'Changing' : 'Change password'}</Button></div>
          </form>
        </Panel>
      </div>
    </>
  )
}



type ZoneView = {
  current: { id: string; zone: string; zone_id: string; trade: string; cities: string[]; neighborhoods: string[]; zips: string[]; decided_at: string | null }[]
  requests: { id: string; zone: string; trade: string; status: 'requested' | 'declined'; note: string | null; decision_note: string | null; requested_at: string }[]
  zones: { id: string; name: string; cities: string[]; taken: boolean }[]
}

/**
 * The business's Service Zone: always shown (Business Terms 2.6), and a way to
 * ask for a change. Taken zones show as taken; who holds them is never shown.
 */
function ServiceZone() {
  const { data, reload } = useApi<ZoneView>('/api/client/zones')
  const toast = useToast()
  const [want, setWant] = useState('')
  const [note, setNote] = useState('')
  if (!data || (!data.zones.length && !data.current.length)) return null
  const waiting = data.requests.find((r) => r.status === 'requested')
  const lastNo = data.requests.find((r) => r.status === 'declined' && r.decision_note !== 'Replaced by a newer request' && r.decision_note !== 'Withdrawn by the business')
  const ask = async () => {
    try { await post('/api/client/zones/request', { zone_id: want, note }); setWant(''); setNote(''); toast('Asked. You will get an email when it is decided.', 'pos'); reload() }
    catch (e) { toast((e as Error).message, 'neg') }
  }
  const withdraw = async (id: string) => {
    try { await del(`/api/client/zones/request/${id}`); toast('Request withdrawn', 'pos'); reload() } catch (e) { toast((e as Error).message, 'neg') }
  }
  return (
    <Panel>
      <div className="panel-header"><h2 className="panel-title">Your service zone</h2><span className="muted">where your leads come from</span></div>
      {data.current.length ? data.current.map((c) => (
        <div key={c.id} style={{ marginBottom: 'var(--s-3)' }}>
          <strong>{c.zone}</strong> <span className="muted">for {c.trade}</span>
          <div className="muted">{[...c.cities, ...c.neighborhoods].join(', ')}{c.zips.length ? ` · ZIP ${c.zips.join(', ')}` : ''}</div>
        </div>
      )) : <p className="muted" style={{ marginTop: 0 }}>No zone yet. Ask for one below.</p>}
      {waiting ? (
        <Banner tone="neutral" title={`You asked for ${waiting.zone}`} action={<Button variant="ghost" onClick={() => withdraw(waiting.id)}>Withdraw</Button>}>
          Waiting for Handpicked to decide. Your current zone stays as it is meanwhile.
        </Banner>
      ) : (
        <div className="stack-tight">
          {lastNo && <span className="muted">Your request for {lastNo.zone} was not approved{lastNo.decision_note ? `: ${lastNo.decision_note}` : ''}.</span>}
          <FormRow label={data.current.length ? 'Ask to change your zone' : 'Ask for a zone'} htmlFor="zone-ask">
            <select id="zone-ask" className="field" value={want} onChange={(e) => setWant(e.target.value)}>
              <option value="">Choose a zone</option>
              {data.zones.map((z) => <option key={z.id} value={z.id} disabled={z.taken && !data.current.some((c) => c.zone_id === z.id)}>{z.name}{z.taken ? ' (taken)' : ''}</option>)}
            </select>
          </FormRow>
          {want && <Field label="Why this zone (optional)" value={note} onChange={(e) => setNote(e.target.value)} />}
          <div><Button onClick={ask} disabled={!want}>Ask</Button></div>
        </div>
      )}
      <p className="muted" style={{ marginBottom: 0 }}>Handpicked may make a zone larger or smaller to send you leads closer to where you do your best work (Business Terms 2.6).</p>
    </Panel>
  )
}
